import {
  getWidgetSession,
} from '../session/widget-session.js';
import { createWidgetMessageEventHandler } from './widget-message-event.js';
import { createBroadcastAuthorizer } from './create-broadcast-authorizer.js';
import { detectReverbApiEnvMismatch } from './reverb-config.js';
import { logRealtime } from './realtime-logger.js';
import { waitForPusherConnected } from './wait-pusher-connected.js';
import {
  normalizeWidgetMessagePayload,
  parseWidgetThreadSignal,
} from './widget-event-payload.js';
import type {
  ReverbClientConfig,
  WidgetRealtimeCallbacks,
} from './types.js';

export type ConnectWidgetRealtimeOptions = {
  apiBase: string;
  reverb: ReverbClientConfig;
  visitorId: string;
  callbacks: WidgetRealtimeCallbacks;
};

type EchoChannel = {
  listenToAll: (
    handler: (eventName: string, payload: unknown) => void,
  ) => void;
  stopListeningToAll: (handler: (eventName: string, payload: unknown) => void) => void;
};

type PusherChannel = {
  bind: (event: string, handler: (data: unknown) => void) => void;
  unbind: (event: string, handler?: (data: unknown) => void) => void;
};

type PusherConnection = {
  bind: (event: string, handler: (data: unknown) => void) => void;
  unbind: (event: string, handler?: (data: unknown) => void) => void;
  state: string;
};

type PusherClient = {
  connection: PusherConnection;
  bind: (event: string, handler: (data: unknown) => void) => void;
  unbind: (event: string, handler?: (data: unknown) => void) => void;
  channel: (name: string) => PusherChannel | null;
};

type EchoConnector = {
  pusher: PusherClient;
};

type EchoClient = {
  private: (channel: string) => EchoChannel;
  leave: (channel: string) => void;
  disconnect: () => void;
  connector: EchoConnector;
};

const WIDGET_MESSAGE_EVENT_SUFFIX = 'NewSiteWidgetMessageEvent';

function isWidgetMessageEventName(eventName: string): boolean {
  return (
    eventName === WIDGET_MESSAGE_EVENT_SUFFIX ||
    eventName === `.${WIDGET_MESSAGE_EVENT_SUFFIX}` ||
    eventName.endsWith(WIDGET_MESSAGE_EVENT_SUFFIX)
  );
}

/**
 * Connect Laravel Reverb + subscribe to `private-site.widget.{visitor_id}.messages`.
 * Dynamically imports `laravel-echo` and `pusher-js` to keep initial bundle smaller.
 */
export async function connectWidgetRealtime(
  options: ConnectWidgetRealtimeOptions,
): Promise<{ disconnect: () => void }> {
  const session = getWidgetSession();
  if (!session?.token) {
    throw new Error('[Teo Widget] Active session token is required for realtime');
  }

  const apiBase = options.apiBase.replace(/\/$/, '');
  const channelName = `site.widget.${options.visitorId}.messages`;
  const privateChannelName = `private-${channelName}`;
  const authEndpoint = `${apiBase}/broadcasting/auth`;

  const envMismatch = detectReverbApiEnvMismatch(apiBase, options.reverb.host);
  if (envMismatch) {
    logRealtime('connect', envMismatch, {
      apiBase,
      reverbHost: options.reverb.host,
      reverbKey: options.reverb.key,
    }, 'error');
  }

  logRealtime('connect', 'starting', {
    apiBase,
    visitorId: options.visitorId,
    channel: privateChannelName,
    reverb: options.reverb,
    authEndpoint,
    sessionId: session.sessionId,
    threadId: session.threadId,
    envMismatch: envMismatch ?? undefined,
  });

  const [{ default: Echo }, { default: Pusher }] = await Promise.all([
    import('laravel-echo'),
    import('pusher-js'),
  ]);

  logRealtime('connect', 'libraries loaded', {
    echo: Boolean(Echo),
    pusher: Boolean(Pusher),
  });

  const win = window as Window & { Pusher?: typeof Pusher };
  win.Pusher = Pusher;

  const echo = new Echo({
    broadcaster: 'reverb',
    key: options.reverb.key,
    wsHost: options.reverb.host,
    wsPort: options.reverb.port,
    wssPort: options.reverb.port,
    forceTLS: options.reverb.scheme === 'https',
    enabledTransports: ['ws', 'wss'],
    bearerToken: session.token,
    authEndpoint,
    auth: {
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${session.token}`,
        'X-Requested-With': 'XMLHttpRequest',
      },
    },
    authorizer: createBroadcastAuthorizer(
      apiBase,
      () => getWidgetSession()?.token,
    ),
  }) as unknown as EchoClient;

  const pusher = echo.connector?.pusher;
  const connectionHandlers: Array<{
    event: string;
    handler: (data: unknown) => void;
  }> = [];
  const channelHandlers: Array<{
    event: string;
    handler: (data: unknown) => void;
  }> = [];

  if (pusher) {
    let sawConnected = false;

    const bindConnection = (event: string) => {
      const handler = (data: unknown) => {
        logRealtime('pusher', event, data);

        if (event === 'state_change') {
          const states = data as { previous?: string; current?: string };
          const current = states.current ?? 'unknown';
          const previous = states.previous;
          options.callbacks.onConnectionStateChange?.(current, previous);

          if (current === 'connected') {
            if (
              sawConnected &&
              (previous === 'disconnected' ||
                previous === 'unavailable' ||
                previous === 'connecting')
            ) {
              logRealtime('connect', 'reconnected', { previous, current });
              options.callbacks.onReconnected?.();
            }
            sawConnected = true;
          }
        }
      };
      pusher.connection.bind(event, handler);
      connectionHandlers.push({ event, handler });
    };

    bindConnection('state_change');
    bindConnection('connected');
    bindConnection('disconnected');
    bindConnection('error');
    bindConnection('unavailable');

    const subscriptionSucceededHandler = (data: unknown) => {
      logRealtime('pusher', 'global:pusher:subscription_succeeded', data, 'debug');
    };
    const subscriptionErrorHandler = (data: unknown) => {
      logRealtime('pusher', 'global:pusher:subscription_error', data, 'error');
    };
    pusher.bind('pusher:subscription_succeeded', subscriptionSucceededHandler);
    pusher.bind('pusher:subscription_error', subscriptionErrorHandler);
    connectionHandlers.push({
      event: 'pusher:subscription_succeeded',
      handler: subscriptionSucceededHandler,
    });
    connectionHandlers.push({
      event: 'pusher:subscription_error',
      handler: subscriptionErrorHandler,
    });

    logRealtime('connect', 'pusher initial state', {
      state: pusher.connection.state,
    });

    await waitForPusherConnected(pusher.connection);
  }

  const channel = echo.private(channelName);

  if (pusher) {
    const pusherChannel = pusher.channel(privateChannelName);
    if (pusherChannel) {
      const onSubOk = () => {
        logRealtime('channel', 'subscription_succeeded', {
          channel: privateChannelName,
        });
      };
      const onSubErr = (status: unknown) => {
        logRealtime('channel', 'subscription_error', {
          channel: privateChannelName,
          status,
        }, 'error');
      };
      pusherChannel.bind('pusher:subscription_succeeded', onSubOk);
      pusherChannel.bind('pusher:subscription_error', onSubErr);
      channelHandlers.push({ event: 'pusher:subscription_succeeded', handler: onSubOk });
      channelHandlers.push({ event: 'pusher:subscription_error', handler: onSubErr });
    }
  }

  const { handle, handleThreadSignal, cancelPendingRefetch } =
    createWidgetMessageEventHandler(options.callbacks);

  const seenEventKeys = new Set<string>();
  const maxSeenKeys = 200;

  const onChannelEvent = (eventName: string, rawPayload: unknown) => {
    logRealtime('channel', `event received: ${eventName}`, rawPayload);

    if (!isWidgetMessageEventName(eventName)) {
      logRealtime(
        'channel',
        `ignored non-widget event: ${eventName}`,
        rawPayload,
        'debug',
      );
      return;
    }

    const payload = normalizeWidgetMessagePayload(rawPayload);
    if (payload?.message) {
      const dedupeKey = `${payload.message.id}:${payload.message.updated_at}`;
      if (seenEventKeys.has(dedupeKey)) {
        logRealtime(
          'handler',
          'duplicate event skipped',
          { dedupeKey, eventName },
          'debug',
        );
        return;
      }

      seenEventKeys.add(dedupeKey);
      if (seenEventKeys.size > maxSeenKeys) {
        const first = seenEventKeys.values().next().value;
        if (first) seenEventKeys.delete(first);
      }

      handle(payload);
      return;
    }

    const signal = parseWidgetThreadSignal(rawPayload);
    if (signal) {
      handleThreadSignal(signal.threadId);
      return;
    }

    logRealtime(
      'channel',
      'ignored widget event: unrecognized payload',
      rawPayload,
      'warn',
    );
  };

  channel.listenToAll(onChannelEvent);

  logRealtime('connect', 'private channel subscribe requested', {
    channel: privateChannelName,
    authEndpoint,
  });

  return {
    disconnect: () => {
      logRealtime('connect', 'disconnecting', { channel: privateChannelName });
      cancelPendingRefetch();
      channel.stopListeningToAll(onChannelEvent);
      echo.leave(privateChannelName);
      echo.disconnect();

      if (pusher) {
        const pusherChannel = pusher.channel(privateChannelName);
        for (const { event, handler } of channelHandlers) {
          pusherChannel?.unbind(event, handler);
        }
        for (const { event, handler } of connectionHandlers) {
          if (event.startsWith('pusher:')) {
            pusher.unbind(event, handler);
          } else {
            pusher.connection.unbind(event, handler);
          }
        }
      }
    },
  };
}
